import { BaseFeatureToggles } from '../navigation/BaseFeatureToggles';
import type { AssetState, WorkflowGroupOptions } from './types';
import type { Workflow } from './Workflow';
import { WorkflowGroup } from './WorkflowGroup';

export class WorkflowGroupManager {
  public workflowGroupMap: Record<string, WorkflowGroup>;
  public workflowGroups: WorkflowGroup[];
  public workflowMap: Record<string, Workflow>;
  public options: WorkflowGroupOptions[];

  constructor(options: WorkflowGroupOptions[]) {
    this.workflowGroups = [];
    this.workflowGroupMap = {};
    this.workflowMap = {};
    this.options = options;
  }

  public static constructWorkflowGroupMap(workflowGroups: WorkflowGroup[]): Record<string, WorkflowGroup> {
    return workflowGroups.reduce<Record<string, WorkflowGroup>>((prev, curr) => {
      prev[curr.workflowGroupKey] = curr;
      return prev;
    }, {});
  }

  public static constructWorkflowMap(workflowGroups: WorkflowGroup[]): Record<string, Workflow> {
    return workflowGroups.reduce<Record<string, Workflow>>((prev, curr) => {
      curr.workflows.forEach((workflow) => {
        if (prev[workflow.workflowKey]) {
          throw new Error(`Duplicate key ${workflow.workflowKey} detected`);
        }
        prev[workflow.workflowKey] = workflow;
      });
      return prev;
    }, {});
  }
  private hasRequiredWorkflowFeatures(partnerFeatureToggles: Record<string, string>, options: WorkflowGroupOptions) {
    for (const feat in options.requiredFeatures) {
      if (!(feat in partnerFeatureToggles) && options.requiredFeatures[feat] === 'false') {
        continue;
      }
      if (partnerFeatureToggles[feat] !== options.requiredFeatures[feat]) {
        return false;
      }
    }
    return true;
  }
  private hasRequiredShipiumAdminStatus(
    isShipiumAdmin: boolean,
    partnerFeatureToggles: Record<string, string>,
    options: WorkflowGroupOptions,
  ) {
    if (
      !isShipiumAdmin &&
      options.shipiumOnlyFeatureToggleRestricted &&
      partnerFeatureToggles[BaseFeatureToggles.RESTRICT_RATE_SHEET_ACCESS] === 'true'
    ) {
      return false;
    }
    if (!isShipiumAdmin && options.shipiumOnly) {
      return false;
    }
    return true;
  }
  public buildWorkflows(
    assetState: AssetState,
    partnerFeatureToggles: Record<string, string>,
    isShipiumAdmin: boolean,
  ) {
    this.workflowGroups = this.options
      .filter((options) => {
        return (
          this.hasRequiredWorkflowFeatures(partnerFeatureToggles, options) &&
          this.hasRequiredShipiumAdminStatus(isShipiumAdmin, partnerFeatureToggles, options)
        );
      })
      .map((options) => {
        const group = new WorkflowGroup(options, isShipiumAdmin);
        group.buildWorkflows(assetState);
        return group;
      });
    this.workflowGroupMap = WorkflowGroupManager.constructWorkflowGroupMap(this.workflowGroups);
    this.workflowMap = WorkflowGroupManager.constructWorkflowMap(this.workflowGroups);
    return this.workflowMap;
  }

  public initializeWorkflowState() {
    return this.workflowGroups.reduce<Record<string, any>>((prev, cur) => {
      cur.workflows.forEach((workflow) => {
        prev[workflow.workflowKey] = {};
      });
      return prev;
    }, {});
  }

  public initializeWorkflowAssets(): AssetState {
    return {
      carrier: [],
      carrierServiceMethod: [],
      origin: [],
      address: [],
      contact: [],
      tags: [],
      schedule: [],
      carrierAccount: [],
      carrierRate: [],
      returnsSettings: [],
      labelAugmentation: [],
      packaging: [],
      packagingSet: [],
      labelProperties: [],
      fulfillmentContext: [],
      carrierSettings: [],
      carrierRateSet: [],
      contract: [],
      augmentationSet: [],
      fulfillmentContextLinkProperty: [],
      tenant: [],
      thirdPartyBillingAccount: [],
      thirdPartyBillingAccountSet: [],
      returnAddressOverride: [],
      laneSortConfigurations: [],
    };
  }
}
